import { useCallback, useMemo, useState } from "react";

import { Button } from "atoms/button/Button";
import { QuantityStepper } from "components/QuantityStepper";

interface packagePrice {
  priceId: string;
  priceInCents: number;
  quantity?: number | null;
}

interface QuantityAddToCartProps {
  prices: packagePrice[];
  isAvailable: boolean;
  isGuest: boolean;
  onAddToCart: (priceId: string, quantity: number) => void;
  guestButtonLabel?: string;
  availableButtonLabel?: string;
  unavailableButtonLabel?: string;
}

export const QuantityAddToCart = ({
  prices: priceTiers,
  isAvailable,
  isGuest,
  onAddToCart,
  guestButtonLabel = "Sign up to add to cart",
  availableButtonLabel = "Add to Cart",
  unavailableButtonLabel = "Out of Stock",
}: QuantityAddToCartProps) => {
  const [quantity, setQuantity] = useState(1);
  const [isQuantityInvalid, setIsQuantityInvalid] = useState(false);

  const sortedPackagePrices = useMemo(
    () => [...priceTiers].sort((a, b) => (a.quantity || 1) - (b.quantity || 1)),
    [priceTiers]
  );

  const maxQuantity = useMemo(() => {
    if (sortedPackagePrices.length === 0) return 1;
    return Math.max(...sortedPackagePrices.map((tier) => tier.quantity || 1));
  }, [sortedPackagePrices]);

  const currentPriceTier = useMemo(() => {
    const matchingTier = sortedPackagePrices.find(
      (tier) => (tier.quantity || 1) === quantity
    );
    if (matchingTier) return matchingTier;

    const applicablePrices = sortedPackagePrices.filter(
      (tier) => (tier.quantity || 1) <= quantity
    );
    if (applicablePrices.length > 0) {
      return applicablePrices[applicablePrices.length - 1];
    }

    return sortedPackagePrices[0];
  }, [quantity, sortedPackagePrices]);

  const handleQuantityChange = useCallback(
    (newQuantity: number, invalid: boolean) => {
      setQuantity(newQuantity);
      setIsQuantityInvalid(invalid);
    },
    []
  );

  const handleAddToCartClick = useCallback(() => {
    if (!isAvailable || !currentPriceTier || isQuantityInvalid) {
      return;
    }

    onAddToCart(currentPriceTier.priceId, quantity);
  }, [isAvailable, currentPriceTier, isQuantityInvalid, quantity, onAddToCart]);

  const getButtonLabel = () => {
    if (isGuest) return guestButtonLabel;
    if (!isAvailable) return unavailableButtonLabel;
    return availableButtonLabel;
  };

  const isButtonDisabled =
    !isAvailable || isGuest || isQuantityInvalid || !currentPriceTier;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-center">
        <QuantityStepper
          disabled={!isAvailable}
          defaultValue={1}
          maxValue={maxQuantity}
          onChange={handleQuantityChange}
        />
      </div>

      <Button
        fullWidth
        disabled={isButtonDisabled}
        onClick={handleAddToCartClick}
      >
        {getButtonLabel()}
      </Button>
    </div>
  );
};
